import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import { getApiUrl } from '@/utils/api';

const API = getApiUrl();

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?featured=true`);
        setFeaturedProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-grid" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1767897213817-8664d9b82393?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt="Hero Background"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black" />
        </div>

        {/* Marquee Banner */}
        <div className="absolute top-24 left-0 right-0 bg-primary py-2 overflow-hidden z-10">
          <div className="marquee">
            <div className="marquee-content">
              <span className="font-heading text-black font-bold text-sm uppercase tracking-widest px-8">EDIÇÃO LIMITADA • 3 POR R$199,90 • PROMOÇÃO DE LANÇAMENTO • </span>
              <span className="font-heading text-black font-bold text-sm uppercase tracking-widest px-8">EDIÇÃO LIMITADA • 3 POR R$199,90 • PROMOÇÃO DE LANÇAMENTO • </span>
              <span className="font-heading text-black font-bold text-sm uppercase tracking-widest px-8">EDIÇÃO LIMITADA • 3 POR R$199,90 • PROMOÇÃO DE LANÇAMENTO • </span>
            </div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-6 leading-tight" data-testid="hero-title">
              DEFINA SEU
              <br />
              <span className="text-primary text-glow">ESTILO</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-zinc-300 mb-8 max-w-2xl mx-auto">
              Camisetas exclusivas com estampas ousadas para quem vive a cultura streetwear sem limites.
            </p>
            
            <Link to="/catalog" data-testid="hero-cta-button">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary text-black font-bold uppercase tracking-wider px-8 py-4 text-lg hover:bg-white transition-colors inline-flex items-center gap-3 neon-glow"
              >
                Explorar Coleção
                <ArrowRight className="h-5 w-5" />
              </motion.button>
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-primary rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Featured Products Section */}
      <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
              data-testid="featured-products-title"
            >
              PRODUTOS EM <span className="text-primary">DESTAQUE</span>
            </motion.h2>
            <p className="text-zinc-400 text-lg">As estampas mais vendidas da temporada</p>
          </div>

          {loading ? (
            <div className="text-center py-12" data-testid="loading-indicator">
              <div className="inline-block h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-products-grid">
              {featuredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/catalog" data-testid="view-all-button">
              <motion.button
                whileHover={{ scale: 1.05 }}
                className="border border-white/20 text-white hover:bg-white hover:text-black transition-colors uppercase tracking-wider px-8 py-4"
              >
                Ver Tudo
              </motion.button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-4" data-testid="footer">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="font-heading text-2xl font-bold text-primary mb-4">DON'T DISTURB</h3>
          <p className="text-zinc-400 text-sm">&copy; 2026 Don't Disturb Store. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;